<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useMaterialStore } from '@/stores/materialStore'
import { useConfigStore } from '@/stores/configStore'
import { dataService } from '@/services/DataService'
import { MaterialEntry } from '@/types/material.types'
import ColorSwatch from './ColorSwatch.vue'
import LoadingSpinner from '../common/LoadingSpinner.vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'

defineOptions({
  name: 'ColorContent'
})

// Configuration Swiper
const swiperModules = [Pagination]

const materialStore = useMaterialStore()
const configStore = useConfigStore()

// Computed pour les couleurs - toujours afficher les couleurs extérieures
// L'intérieur est maintenant automatiquement sélectionné via le mapping couleur→intérieur
const colors = computed(() => {
  return materialStore.exteriorColors
})

// Ordre d'affichage personnalisé pour les teintes
const UNI_COLOR_ORDER = ['BC_ZHJ', 'BC_WBF', 'BC_WB3', 'BC_WA1', 'BC_WBH', 'BC_WBE']
const SOCOLOR_COLOR_ORDER = ['BC_E86', 'BC_FDC', 'BC_FFZ', 'BC_E5G', 'BC_E85']

// Fonction de tri personnalisée selon l'ordre spécifié
const sortByCustomOrder = <T extends { material: string }>(items: T[], order: string[]): T[] => {
  // Fonction pour extraire le code couleur (BC_XXX) du material
  const extractColorCode = (material: string): string => {
    const match = material.match(/BC_[A-Z0-9]+/)
    return match ? match[0] : material
  }
  
  return [...items].sort((a, b) => {
    const colorCodeA = extractColorCode(a.material)
    const colorCodeB = extractColorCode(b.material)
    
    const indexA = order.indexOf(colorCodeA)
    const indexB = order.indexOf(colorCodeB)
    
    // Les éléments dans l'ordre ont priorité
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB
    }
    // Les éléments dans l'ordre viennent en premier
    if (indexA !== -1) return -1
    if (indexB !== -1) return 1
    // Sinon, trier alphabétiquement
    return colorCodeA.localeCompare(colorCodeB)
  })
}

// Couleurs triées par type de teinte avec ordre personnalisé
const uniColors = computed(() => {
  const filtered = colors.value.filter(c => c.teinteType === 'uni')
  return sortByCustomOrder(filtered, UNI_COLOR_ORDER)
})

const socolorColors = computed(() => {
  const filtered = colors.value.filter(c => c.teinteType === 'socolor')
  return sortByCustomOrder(filtered, SOCOLOR_COLOR_ORDER)
})

// Couleurs sans type défini (fallback)
const untypedColors = computed(() => {
  return colors.value.filter(c => !c.teinteType)
})

// Fonction pour obtenir l'URL de l'image de la teinte
const getTeinteImageUrl = (color: MaterialEntry): string => {
  // Priorité 1: imageUrl explicite
  if (color.imageUrl) {
    return color.imageUrl;
  }
  
  // Priorité 2: utiliser directement le material (ex: "BC_WA1")
  if (color.material && color.material.startsWith('BC_')) {
    return `/teintes/${color.material}.png`;
  }
  
  return '';
}

// Vérifie si on doit afficher les sections séparées
const showTeinteSections = computed(() => {
  return uniColors.value.length > 0 || socolorColors.value.length > 0
})

// La couleur sélectionnée est toujours la couleur extérieure
const selectedColor = computed(() => {
  return materialStore.selectedExteriorColor
})

const isLoading = computed(() => configStore.isLoading || materialStore.isLoading)

// Fonctions de sélection - toujours sélectionner la couleur extérieure
// L'intérieur correspondant est automatiquement sélectionné via le mapping dans materialStore
const selectColor = (colorId: string): void => {
  try {
    materialStore.selectExteriorColor(colorId)
    configStore.selectExteriorColor(colorId)
  } catch (error) {
    console.error('Erreur lors de la sélection de la couleur:', error)
  }
}

// Fonction pour initialiser la sélection par défaut avec la première teinte unie
const initializeDefaultColorSelection = (): void => {
  // Si aucune couleur n'est sélectionnée, sélectionner la première teinte unie
  if (!selectedColor.value && uniColors.value.length > 0) {
    const firstUniColor = uniColors.value[0]
    if (firstUniColor) {
      selectColor(firstUniColor.material)
    }
  }
}

// Initialiser au montage du composant
onMounted(() => {
  initializeDefaultColorSelection()
})

// Watcher pour gérer le cas asynchrone (données chargées après montage)
watch(
  () => colors.value,
  (newColors) => {
    if (newColors.length > 0 && !selectedColor.value) {
      initializeDefaultColorSelection()
    }
  },
  { immediate: true }
)

const isSelected = (colorId: string): boolean => {
  return selectedColor.value === colorId
}

const getColorValue = (color: MaterialEntry): string => {
  return color.material
}

// Émettre un événement pour naviguer vers l'étape suivante
const emit = defineEmits(['nextStep'])

function handleFinalize() {
  emit('nextStep')
}

/**
 * Retourne le label de la teinte sélectionnée avec son prix formaté
 * Ex: "Land Breeze Green Pearl Metallic - 750 €"
 * Si le prix est 0 ou vide, retourne uniquement le label
 */
const getSelectedColorLabelWithPrice = (): string => {
  if (!selectedColor.value) return ''

  // Extraire le code couleur du path (ex: BC_WB3 de EXT-GRD_P10GL-BG_A-BC_WB3)
  const colorCodeMatch = selectedColor.value.match(/(BC_|INTBASE_)[A-Z0-9_]+/)
  const colorCode = colorCodeMatch ? colorCodeMatch[0] : null

  if (!colorCode) {
    return selectedColor.value
  }

  // Récupérer les items pour trouver le label et le prix
  let label = colorCode
  let price = 0

  try {
    const itemsConfig = dataService.getItems()

    // Chercher l'item correspondant dans la catégorie BODYCOLOR (couleurs extérieures)
    for (const category of itemsConfig.ITEMS) {
      if (category.category_id === 'BODYCOLOR') {
        for (const group of category.groups) {
          const item = group.items.find(i => i.item_id === colorCode)
          if (item) {
            label = item.label || colorCode
            if (typeof item.price === 'number') {
              price = item.price
            }
            break
          }
        }
      }
    }
  } catch (error) {
    console.warn('Erreur lors de la récupération du prix de la teinte:', error)
  }

  // Retourner avec ou sans prix
  if (price > 0) {
    return `${label} - ${price.toLocaleString('fr-FR')} €`
  }
  return label
}
</script>

<template>
  <div class="color-content">
    <p>Choisissez parmi les teintes disponibles :</p>
    <div v-if="isLoading" class="loading-state">
      <LoadingSpinner size="small" />
      <span>Chargement des couleurs...</span>
    </div>

    <div v-else-if="colors.length === 0" class="empty-state">
      <p>Aucune couleur disponible pour cette version.</p>
    </div>

    <!-- Affichage avec sections uni/socolor -->
    <div v-else-if="showTeinteSections" class="teintes-container">

      <!-- Section Teintes Uni -->
      <div v-if="uniColors.length > 0" class="teinte-section">
        <h3 class="teinte-title">unies</h3>
        <Swiper
          :modules="swiperModules"
          :slides-per-view="'auto'"
          :space-between="0"
          :watch-overflow="true"
          :center-insufficient-slides="true"
          :observer="true"
          :observe-parents="true"
          :navigation="false"
          :pagination="{ clickable: true }"
          class="color-swiper"
                    role="radiogroup"
          aria-label="Sélection de teinte unie extérieure"
        >
          <SwiperSlide
            v-for="color in uniColors"
            :key="color.material"
            class="color-slide"
          >
            <ColorSwatch
              :color="getColorValue(color)"
              :label="color.material"
              :image-url="getTeinteImageUrl(color)"
              :selected="isSelected(color.material)"
              :disabled="false"
              size="medium"
              @click="selectColor(color.material)"
            />
          </SwiperSlide>
        </Swiper>
      </div>

      <!-- Section So'Color -->
      <div v-if="socolorColors.length > 0" class="teinte-section socolor-section">
        <h3 class="teinte-title">So'Color</h3>
        <Swiper
          :modules="swiperModules"
          :slides-per-view="'auto'"
          :space-between="0"
          :watch-overflow="true"
          :center-insufficient-slides="true"
          :observer="true"
          :observe-parents="true"
          :navigation="false"
          :pagination="{ clickable: true }"
          class="color-swiper"
                    role="radiogroup"
          aria-label="Sélection de teinte so'color extérieure"
        >
          <SwiperSlide
            v-for="color in socolorColors"
            :key="color.material"
            class="color-slide"
          >
            <ColorSwatch
              :color="getColorValue(color)"
              :label="color.material"
              :image-url="getTeinteImageUrl(color)"
              :selected="isSelected(color.material)"
              :disabled="false"
              size="medium"
              @click="selectColor(color.material)"
            />
          </SwiperSlide>
        </Swiper>
      </div>

      <!-- Couleurs sans type (fallback) -->
      <div v-if="untypedColors.length > 0" class="teinte-section fallback-section">
        <h3 class="teinte-title">Autres teintes</h3>
        <Swiper
          :modules="swiperModules"
          :slides-per-view="'auto'"
          :space-between="0"
          :watch-overflow="true"
          :center-insufficient-slides="true"
          :observer="true"
          :observe-parents="true"
          :navigation="false"
          :pagination="{ clickable: true }"
          class="color-swiper"
                    role="radiogroup"
          aria-label="Sélection de teinte extérieure"
        >
          <SwiperSlide
            v-for="color in untypedColors"
            :key="color.material"
            class="color-slide"
          >
            <ColorSwatch
              :color="getColorValue(color)"
              :label="color.material"
              :selected="isSelected(color.material)"
              :disabled="false"
              size="medium"
              @click="selectColor(color.material)"
            />
          </SwiperSlide>
        </Swiper>
      </div>
    </div>

    <!-- Affichage legacy (sans type) -->
    <Swiper
      v-else
      :modules="swiperModules"
      :slides-per-view="'auto'"
      :space-between="0"
      :watch-overflow="true"
      :center-insufficient-slides="true"
      :observer="true"
      :observe-parents="true"
      :navigation="false"
      :pagination="{ clickable: true }"
      class="color-swiper"
            role="radiogroup"
      aria-label="Sélection de couleur extérieure"
    >
      <SwiperSlide
        v-for="color in colors"
        :key="color.material"
        class="color-slide"
      >
        <ColorSwatch
          :color="getColorValue(color)"
          :label="color.material"
          :selected="isSelected(color.material)"
          :disabled="false"
          size="medium"
          @click="selectColor(color.material)"
        />
      </SwiperSlide>
    </Swiper>

    <div v-if="selectedColor" class="selected-color-info">
      <p>
        <strong>Teinte :</strong>
        {{ getSelectedColorLabelWithPrice() }}
      </p>
    </div>

    <div class="finalize-section">
      <button
        class="precommand-link"
        @click="handleFinalize"
        :disabled="!selectedColor"
      >
        Finaliser
      </button>
    </div>
  </div>
</template>

<style scoped>

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px;
  color: #6c757d;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #6c757d;
}

.teintes-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.teinte-section {
  display: flex;
}

.teinte-title {
  font-family: 'SuzukiPROHeadline', sans-serif;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  background-color: #454a46;
  color: var(--color-text-primary);
  margin:0;
  padding: 0.5em 0.25em;
  writing-mode: sideways-lr;
  text-align: center;
}

.fallback-section .teinte-title {
  color: #6c757d;
}

/* Conteneur principal - empêche le débordement */
.color-content {
  overflow: hidden;
  max-width: 100%;
}

/* Swiper carousel pour les couleurs */
.color-swiper {
  background-color: var(--tab-default);
  flex: 1;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

/* Slide avec largeur fixe de 110px */
.color-slide {
  width: 110px;
  flex-shrink: 0;
}

/* Style de la pagination */
.color-swiper :deep(.swiper-pagination) {
  bottom: 0;
}

.color-swiper :deep(.swiper-pagination-bullet) {
  background: var(--color-text-primary);
  opacity: 0.3;
}

.color-swiper :deep(.swiper-pagination-bullet-active) {
  opacity: 1;
}

/* Masquer la pagination quand swiper est verrouillé (watchOverflow) */
.color-swiper.swiper-locked :deep(.swiper-pagination) {
  display: none;
}

/* Styles spécifiques pour la vue intérieure - miniatures 92x92px */
.color-swiper.interior-view .color-slide {
  width: 92px;
}

.color-swiper.interior-view :deep(.color-swatch) {
  width: 92px;
  height: 92px;
  padding: 4px;
}

.color-swiper.interior-view :deep(.swatch-color) {
  width: 100%;
  height: 100%;
}

.selected-color-info {
  margin-block-start: 1rem;
  padding: 0;
}

.selected-color-info p {
  margin: 0;
  font-size: 1rem;
  color: var(--color-text-primary);
}

/* Section du bouton Finaliser */
.finalize-section {
  padding: 1rem 0 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

/* Style du bouton Finaliser (même que precommand-link) */
.precommand-link {
  padding: 0.5em 3em;
  background-color: var(--color-text-primary);
  color: var(--color-primary);
  border: none;
  border-radius: 0.25rem;
  font-size: 1rem;
  font-weight: 300;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease;
  letter-spacing: 0.5px;
  text-decoration: none;
}

.precommand-link:hover:not(:disabled) {
  background-color: var(--color-hover);
  color: var(--color-primary);
}

.precommand-link:active:not(:disabled) {
  color: var(--color-primary);
}

.precommand-link:disabled {
  background-color: var(--color-disabled, #999);
  color: var(--color-disabled-text, #666);
  cursor: not-allowed;
  pointer-events: none;
}

/* Responsive */
@media (max-width: 768px) {
  .teinte-section {
    padding: 0 10px;
  }

  .color-swiper {
    padding-bottom: 1.5rem;
  }

  .selected-color-info {
    margin: 20px 10px 0;
  }

  .finalize-section {
    padding: 20px 16px 0;
  }

  .precommand-link {
    padding: 16px 24px;
  }
}

@media (max-width: 480px) {
  .finalize-section {
    padding: 16px 12px 0;
  }

  .precommand-link {
    padding: 14px 20px;
  }
}
</style>
